import React, { PureComponent } from 'react';
import { H2, H5 } from '@hobs/components/Headings';
import Panel from '@hobs/components/Panel';
import { Formik, Field } from 'formik';
import PropTypes from 'prop-types';
import Toast from '@hobs/components/Toast';
import objectPath from 'object-path';
import ShortId from 'shortid';
import Autocomplete from '@hobs/components/Autocomplete';
import Textarea from '@hobs/components/Textarea';
import DropZone from '@hobs/components/DropZone';
import Confirm from '@hobs/components/Confirm';
import Icon from '@hobs/components/Icon';
import InputLabel from '@hobs/components/InputLabel';
import { format } from '@hobs/utils/formatWithTimeZone';
import {
  USER_ROLES,
  SERVICE_REQUEST_NAVIGATION_ORIGIN,
  SERVICE_REQUEST_FOOTER_STATUSES,
  DATE_FORMAT,
  PARTY_TYPE
} from '@hobs/utils/constants';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import PageActions from '@hobs/components/PageActions';
import * as Yup from 'yup';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import CalculateByDays from '@hobs/utils/calculateByDays';
import FieldHistoryMessage from '@hobs/components/FieldHistoryMessage';
import PageHeader from '@hobs/components/PageHeader';
import AssignmentResponse from '@hobs/components/AssignmentResponse';
import CatalogItemInfo from '@hobs/components/CatalogItemInfo';
import DeliveryInformation from '@hobs/components/DeliveryInformation';
import LocationDetails from '@hobs/components/LocationDetails';
import PageLinks from '@hobs/components/PageLinks';
import ServiceRequestFooter from '@hobs/components/ServiceRequestFooter';
import ChangeRequestCreateDialog from '@hobs/containers/ChangeRequestCreateDialog';
import ActionWithCommentDialog from '@hobs/containers/ActionWithCommentDialog';
import CancellationCosts from '@hobs/components/CancellationCosts';
import GeneralAttachments from '../GeneralAttachments/GeneralAttachments';

import './ServiceRequestEdit.scss';
import AssignmentDelivery from '@hobs/components/AssignmentDelivery';

class ServiceRequestEdit extends PureComponent {
  constructor(props) {
    super(props);

    const { t } = this.props;

    this.state = {
      attachments: [],
      showChangeItem: false,
      isEarlyConfirmationSubmitting: false,
      serviceRequest: {},
      navigation: {},
      oDataSearchFilter: {},
      dataState: { take: 10, skip: 0, sort: [{ field: 'Code', dir: 'asc' }] },
      disabled: true,
      responseFileDialogOpen: false,
      deliveryFileDialogOpen: false,
      cancellationFileDialogOpen: false,
      itemChanged: false,
      showCreateChangeRequestDialog: false,
      showRequestCancellationDialog: false,
      ...this.props.location.state,
      exerciseCurrency: ''
    };

    this.container = React.createRef();
    this.validationSchema = Yup.object().shape({
      unitId: Yup.string().required(t('Unit is required')),
      unitOfMeasureId: Yup.string().required(t('UoM is required')),
      dateFrom: Yup.date()
        .required(t('From date is required'))
        .typeError(t('From date is required'))
        .nullable(),
      quantity: Yup.number()
        .min(0, t('Quantity must be greater than 0'))
        .required(t('Quantity is required'))
        .typeError(t('Invalid number')),
      comment: Yup.string()
        .max(1500, t('Comment cannot be longer than 1500 symbols'))
        .nullable()
    });
  }

  async componentDidMount() {
    await this.loadComponentData();
  }

  async componentDidUpdate(prevProps) {
    const { match } = this.props;
    if (
      match.params.serviceRequestId !== prevProps.match.params.serviceRequestId
    ) {
      await this.loadComponentData();
    }
  }

  navigateBack = navContext => () => {
    const { history } = this.props;
    history.push(this.getBackNavigationUrl(navContext));
  };

  isOnlyPreviewMode() {
    const { hasUserPermission, serviceRequest } = this.props;
    return (
      hasUserPermission(USER_ROLES.READONLY) ||
      (hasUserPermission(USER_ROLES.HOST_UNIT) &&
        !serviceRequest.canHostUnitEdit)
    );
  }

  loadComponentData = async () => {
    const {
      match,
      getServiceRequest,
      forSubmit,
      getFooter,
      setAttachments
    } = this.props;

    await forSubmit(match.params.partyId);
    await getServiceRequest(match.params.serviceRequestId).then(
      ({ payload }) => {
        setAttachments(payload.attachments);
        getFooter(match.params.serviceRequestId);
        this.setState({
          serviceRequest: {
            ...payload,
            originalIsTransportation: payload.isTransportation
          },
          disabled:
            !payload.canBeEdited ||
            this.isOnlyPreviewMode() ||
            this.isEarlyConfirmationRequiredAndNotEditable(payload)
        });
      }
    );
    this.queryOdata();
  };

  isEarlyConfirmationRequiredAndNotEditable(payload) {
    return (
      payload.response &&
      payload.response.earlyConfirmation.isEarlyConfirmationRequired &&
      !payload.response.earlyConfirmation.isEditable
    );
  }

  getServiceRequestsGridUrl() {
    return `/service-requests/${this.props.match.params.partyId}`;
  }

  getCatalogItemsQueryUrl() {
    return `CatalogItemsQueryForServiceRequest(${this.props.match.params.partyId})`;
  }

  getHistoryPath() {
    const { match } = this.props;
    return `/service-request-history/${match.params.partyId}/${match.params.serviceRequestId}`;
  }

  getEditPath() {
    const { match } = this.props;
    return `/service-request/${match.params.partyId}/${match.params.serviceRequestId}`;
  }

  getShowPath() {
    const { match } = this.props;
    return `/request-information/${match.params.serviceRequestId}`;
  }

  getTransportationPath() {
    const { match } = this.props;
    return `/service-request/${match.params.partyId}/${match.params.serviceRequestId}/transportation`;
  }

  getChangeHistoryPath() {
    const { match } = this.props;
    return `/service-request/${match.params.partyId}/${match.params.serviceRequestId}/change-history`;
  }

  getDiscussionInitials() {
    const { t } = this.props;
    return t('SN');
  }

  getServiceRequestParent() {
    const { match } = this.props;
    const { serviceRequest } = this.state;

    return serviceRequest.parentCategory
      ? {
          to: `/service-request/${match.params.partyId}/${serviceRequest.parentCategory.serviceRequestId}`,
          title: serviceRequest.parentCategory.name
        }
      : null;
  }

  isAdditionalRequest() {
    const { serviceRequest } = this.state;

    return serviceRequest.isAdditionalRequest;
  }

  componentWillUnmount() {
    const { resetOData, clearAttachments, clearFooter } = this.props;
    resetOData();
    clearAttachments();
    clearFooter();

    CalculateByDays.reset();
  }

  handleFileUpload = value => {
    if (value) {
      this.props.uploadAttachments({ attachments: value });
    }
  };

  onSubmit = (values, { setErrors, setSubmitting }) => {
    const { t, match, attachments, history } = this.props;
    const {
      catalogItemId,
      unitOfMeasureId,
      statementOfRequirementId,
      unitId,
      activityId,
      quantity,
      dateFrom,
      dateTo,
      isCalculatedByDays,
      isAccommodation,
      isMeal,
      comment,
      locationComment,
      location = {
        original: {
          name: values.locationName,
          latitude: values.latitude,
          longtitude: values.longitude,
          site: values.site,
          region: {
            name: values.regionName,
            regionId: values.regionId
          }
        }
      },
      locationCommentFrom,
      locationFrom = {
        original: {
          name: values.locationNameFrom,
          latitude: values.latitudeFrom,
          longtitude: values.longitudeFrom,
          site: values.siteFrom,
          region: {
            name: values.regionNameFrom,
            regionId: values.regionIdFrom
          }
        }
      }
    } = values;

    if (
      values.dateTo &&
      new Date(format(Date.parse(values.dateFrom), DATE_FORMAT)) >
        new Date(format(Date.parse(values.dateTo), DATE_FORMAT))
    ) {
      Toast.warning(t('From date must be before To date'));
      setSubmitting(false);
      return;
    }
    this.props
      .update({
        serviceRequestId: match.params.serviceRequestId,
        catalogItemId,
        unitOfMeasureId,
        statementOfRequirementId,
        unitId,
        requestedQuantity: quantity,
        dateFrom,
        dateTo,
        isCalculatedByDays,
        isAccommodation,
        isMeal,
        regionId: objectPath.get(location, 'original.region.regionId', null),
        activityId,
        regionName: objectPath.get(location, 'original.region.name', null),
        locationName: objectPath.get(location, 'original.name', null),
        locationComment,
        site: objectPath.get(location, 'original.site', null),
        latitude: objectPath.get(location, 'original.latitude', null),
        longitude: objectPath.get(location, 'original.longtitude', null),
        // from
        regionIdFrom: objectPath.get(
          locationFrom,
          'original.region.regionId',
          null
        ),
        regionNameFrom: objectPath.get(
          locationFrom,
          'original.region.name',
          null
        ),
        locationNameFrom: objectPath.get(locationFrom, 'original.name', null),
        locationCommentFrom,
        siteFrom: objectPath.get(locationFrom, 'original.site', null),
        latitudeFrom: objectPath.get(locationFrom, 'original.latitude', null),
        longitudeFrom: objectPath.get(
          locationFrom,
          'original.longtitude',
          null
        ),
        comment,
        attachments: attachments
          ? attachments.map(attachment => attachment.attachmentId)
          : []
      })
      .then(response => {
        if (response.success) {
          Toast.success(t('Service request updated successfully'));
          history.push(this.getBackNavigationUrl(this.state.navigation));
        } else {
          if (!response.payload.details) {
            Toast.warning(t(response.payload.message));
          } else {
            const errors = response.payload.details.reduce((results, error) => {
              return {
                ...results,
                [error.target]: error.message[0]
              };
            }, {});
            if (errors) {
              setErrors(errors);
            }
          }
        }

        setSubmitting(false);
      });
  };

  _remove() {
    const { match, remove } = this.props;

    return remove(match.params.partyId, match.params.serviceRequestId);
  }

  onRemove = () => {
    const { t } = this.props;
    Confirm(
      t('Delete service request'),
      t(
        'Are you sure you want to delete this Service request? It will be removed from your SOR.'
      ),
      () => {
        const { history } = this.props;

        this._remove().then(response => {
          if (response.success) {
            Toast.success(t('Service request was successfully deleted'));
            history.push(this.getServiceRequestsGridUrl());
          } else {
            Toast.warning(response.payload.message);
          }
        });
      }
    );
  };

  toggleChangeItem = () => {
    const { t } = this.props;
    const { serviceRequest } = this.state;

    if (serviceRequest.catalogItemCanBeChanged) {
      this.setState(prevState => ({
        showChangeItem: !prevState.showChangeItem
      }));
    } else if (serviceRequest.catalogItemChangeErrorMessage) {
      Toast.warning(serviceRequest.catalogItemChangeErrorMessage);
    } else {
      Toast.warning(
        t(
          'This service request is already split to other service requests and cannot be changed anymore'
        )
      );
    }
  };

  changeItem = ({ original }, setFieldValue) => {
    const uomDefault = original.UnitsOfMeasure.find(item => item.IsDefault);
    const isAccommodationOrMeal = original.IsMeal || original.IsAccommodation;
    this.setState(
      {
        itemChanged: true,
        showChangeItem: false,
        serviceRequest: {
          ...this.state.serviceRequest,
          annexId: original.AnnexId,
          annexName: original.AnnexName,
          attachments: original.catalogItemAttachments,
          catalogItemId: original.CatalogItemId,
          code: original.Code,
          description: original.Description,
          groupName: original.GroupName,
          name: original.Name,
          reateFrom: original.RateFrom,
          reateTo: original.RateTo,
          unitOfMeasureCode: objectPath.get(
            uomDefault,
            'UnitOfMeasureCode',
            '-'
          ),
          unitOfMeasureId: uomDefault ? uomDefault.UnitOfMeasureId : null,
          unitsOfMeasure: original.UnitsOfMeasure,
          isTransportation: original.IsTransportation,
          isAccommodation: original.IsAccommodation,
          isMeal: original.IsMeal,
          isCalculatedByDays: isAccommodationOrMeal,
          catalogItemAttachments: original.CatalogAttachments.map(item => ({
            attachmentId: item.AttachmentId,
            name: item.Name
          }))
        }
      },
      () => {
        setFieldValue('unitOfMeasureId', uomDefault.UnitOfMeasureId);
        setFieldValue('annexId', original.AnnexId);
        setFieldValue('itemChanged', 'true');
      }
    );
  };

  queryOdata() {
    const { oDataSearchFilter, dataState } = this.state;
    const filters = [];

    const params = {
      ...dataState
    };

    if (Object.keys(oDataSearchFilter).length > 0) {
      filters.push(oDataSearchFilter);
    }

    if (filters.length > 0) {
      params.filter = {
        logic: 'and',
        filters
      };
    }

    this.props.queryOData(this.getCatalogItemsQueryUrl(), params);
  }

  toAutocompleteEntity(
    data,
    valueIndex = 'code',
    titleIndex = 'name',
    addServiceCode = false
  ) {
    return data
      ? data.map(item => ({
          value: item[valueIndex],
          title: addServiceCode
            ? `${item[valueIndex]} ${item[titleIndex]}`
            : item[titleIndex],
          original: item
        }))
      : [];
  }

  getUnits = units => {
    const { serviceRequest } = this.state;
    const newUnits = this.flattenUnitsTree(units).map(unit => {
      return {
        title: unit.unitName,
        unitId: unit.unitId
      };
    });
    if (
      serviceRequest &&
      !newUnits.some(unit => unit.unitId === serviceRequest.unitId)
    ) {
      newUnits.push({
        title: serviceRequest.unitName,
        unitId: serviceRequest.unitId
      });
    }

    return this.toUnitsEntity(newUnits);
  };

  flattenUnitsTree(array, level = 0) {
    if (!Array.isArray(array)) {
      return [];
    }

    return array.reduce((result, item) => {
      result.push({
        unitName: `${'\xa0\xa0\xa0'.repeat(level)}${item.unitName}`,
        unitId: item.unitId
      });

      if (Array.isArray(item.units)) {
        result = result.concat(this.flattenUnitsTree(item.units, level + 1));
      }

      return result;
    }, []);
  }

  toUnitsEntity(units) {
    return units
      ? units.map(unit => {
          return {
            title: unit.title,
            value: unit.unitId
          };
        })
      : [];
  }

  toActivityEntity(activities) {
    return activities.map(activity => {
      return {
        name: activity.name,
        activityId: activity.activityId,
        isDefault: activity.isDefault
      };
    });
  }

  toUomsEntity(uoms) {
    return uoms
      ? uoms.map(uom => {
          return {
            title: uom.unitOfMeasureCode || uom.UnitOfMeasureCode,
            value: uom.unitOfMeasureId || uom.UnitOfMeasureId
          };
        })
      : [];
  }

  onItemQueryChange = query => {
    let state = {};

    if (query) {
      state = {
        oDataSearchFilter: {
          logic: 'and',
          filters: [
            {
              field: 'QuickSearchField',
              operator: 'contains',
              value: query
            }
          ]
        }
      };
    } else {
      state = {
        oDataSearchFilter: {}
      };
    }

    this.setState(state, () => {
      this.queryOdata();
    });
  };

  onLocationQueryChange = query => {
    if (this.locationQueryTimeout) {
      clearTimeout(this.locationQueryTimeout);
    }

    this.locationQueryTimeout = setTimeout(() => {
      this.props.queryLocations(query);
    }, 300);
  };

  onResponseFileDialogOpen = () => {
    this.setState({
      responseFileDialogOpen: true
    });
  };

  onResponseFileDialogClose = () => {
    this.setState({
      responseFileDialogOpen: false
    });
  };

  onChangeRequestResponseFileDialogOpen = () => {
    this.setState({
      changeRequestResponseFileDialogOpen: true
    });
  };

  onChangeRequestResponseFileDialogClose = () => {
    this.setState({
      changeRequestResponseFileDialogOpen: false
    });
  };

  calculateCosts(values) {
    if (!values.dateFrom || !values.quantity) {
      return;
    }
    const data = CalculateByDays.calculate(values, {
      RateFrom: values.reateFrom,
      RateTo: values.reateTo
    });

    if (data) {
      this.props.calculateByDays(data);
    }
  }

  cancelForm(dirty, navContext) {
    const { t, history } = this.props;
    if (!dirty) {
      history.push(this.getBackNavigationUrl(navContext));
    } else {
      Confirm(
        t('Cancel service request'),
        t('Are you sure you want to cancel? All entered data will be lost.'),
        () => {
          history.push(this.getBackNavigationUrl(navContext));
        }
      );
    }
  }

  getBackNavigationUrl = navContext => {
    if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview) {
      const { navigationHistory } = this.state;

      const withSearch =
        navigationHistory &&
        navigationHistory.location &&
        navigationHistory.location.search
          ? navigationHistory.location.search
          : '';

      return `/annex-overview${withSearch}`;
    } else if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.Orders) {
      return '/orders';
    } else if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets) {
      return '/tickets';
    } else if (
      navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices
    ) {
      return this.getServiceRequestsGridUrl();
    }

    return this.getServiceRequestsGridUrl();
  };

  getAdditionalCreateWaitingForHnFooter(dirty, isSubmitting, canEditItem) {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Service request is not confirmed yet. Waiting for response from HN.'
        )}
      >
        {canEditItem && (
          <div>
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <GhostButton
                  onClick={() =>
                    this.cancelForm(dirty, nav.serviceRequestNavigation)
                  }
                  large
                >
                  {t('Cancel')}
                </GhostButton>
              )}
            </ServiceRequestNavContext.Consumer>
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <RaisedButton
                  type="submit"
                  disabled={(!dirty && !this.state.itemChanged) || isSubmitting}
                  onClick={() => {
                    this.setState({
                      navigation: nav.serviceRequestNavigation,
                      navigationHistory: nav.previousPageHistory
                    });
                  }}
                  large
                >
                  {t('Save')}
                </RaisedButton>
              )}
            </ServiceRequestNavContext.Consumer>
          </div>
        )}
      </ServiceRequestFooter>
    );
  }

  getAdditionalEditableCreateWaitingForHnFooter(dirty, isSubmitting) {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Service request is not confirmed yet. Waiting for response from HN.'
        )}
      >
        <div>
          <ServiceRequestNavContext.Consumer>
            {nav => (
              <GhostButton
                onClick={() =>
                  this.cancelForm(dirty, nav.serviceRequestNavigation)
                }
                large
              >
                {t('Cancel')}
              </GhostButton>
            )}
          </ServiceRequestNavContext.Consumer>
          <ServiceRequestNavContext.Consumer>
            {nav => (
              <RaisedButton
                type="submit"
                disabled={(!dirty && !this.state.itemChanged) || isSubmitting}
                onClick={() => {
                  this.setState({
                    navigation: nav.serviceRequestNavigation,
                    navigationHistory: nav.previousPageHistory
                  });
                }}
                large
              >
                {t('Save')}
              </RaisedButton>
            )}
          </ServiceRequestNavContext.Consumer>
        </div>
      </ServiceRequestFooter>
    );
  }

  rejectAdditionalRequestResponse = () => {
    const { t, match, createRejectResponse } = this.props;
    Confirm(
      t('Reject response'),
      t(
        "Are you sure, you want to reject HN's response? Service request will be marked as rejected"
      ),
      () => {
        createRejectResponse(match.params.serviceRequestId).then(response => {
          if (response.success) {
            Toast.success(t('Service request successfully removed'));
            this.loadComponentData();
          } else if (response.payload) {
            Toast.warning(response.payload.message);
          } else {
            Toast.warning(t('Cannot reject response'));
          }
        });
      },
      () => {},
      t('Reject'),
      t('Close')
    );
  };

  acceptAdditionalRequestResponse = () => {
    const { t, match, createAcceptResponse } = this.props;
    Confirm(
      t('Accept response'),
      t("Are you sure, you want to accept HN's response? "),
      () => {
        createAcceptResponse(match.params.serviceRequestId).then(response => {
          if (response.success) {
            Toast.success(t('Service request successfully confirmed'));
            this.loadComponentData();
          } else if (response.payload) {
            Toast.warning(response.payload.message);
          } else {
            Toast.warning(t('Cannot accept response'));
          }
        });
      },
      () => {},
      t('Accept'),
      t('Close')
    );
  };

  canUserAcceptResponse() {
    const { hasUserPermission } = this.props;

    return (
      hasUserPermission(USER_ROLES.ADMIN) ||
      hasUserPermission(USER_ROLES.SystemAdmin)
    );
  }

  formHasValueChanges(dirty) {
    return dirty || this.state.itemChanged;
  }

  getAdditionalCreateAcceptFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Please accept or reject HN response to this service request'
        )}
      >
        {this.canUserAcceptResponse() && (
          <>
            <GhostButton
              type="button"
              danger
              large
              onClick={this.rejectAdditionalRequestResponse}
            >
              {t('Reject')}
            </GhostButton>
            <RaisedButton
              type="button"
              large
              onClick={this.acceptAdditionalRequestResponse}
            >
              {t('Accept')}
            </RaisedButton>
          </>
        )}
      </ServiceRequestFooter>
    );
  }

  getAdditionalEditableCreateAcceptFooter(dirty, isSubmitting) {
    const { t } = this.props;

    return (
      <ServiceRequestFooter
        message={t(
          'Please accept or reject HN response to this service request'
        )}
      >
        {!this.formHasValueChanges(dirty) && this.canUserAcceptResponse() && (
          <>
            <GhostButton
              type="button"
              danger
              large
              onClick={this.rejectAdditionalRequestResponse}
            >
              {t('Reject')}
            </GhostButton>
            <RaisedButton
              type="button"
              large
              onClick={this.acceptAdditionalRequestResponse}
            >
              {t('Accept')}
            </RaisedButton>
          </>
        )}
        {this.formHasValueChanges(dirty) && (
          <>
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <GhostButton
                  onClick={() =>
                    this.cancelForm(dirty, nav.serviceRequestNavigation)
                  }
                  large
                >
                  {t('Cancel')}
                </GhostButton>
              )}
            </ServiceRequestNavContext.Consumer>
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <RaisedButton
                  type="submit"
                  disabled={(!dirty && !this.state.itemChanged) || isSubmitting}
                  onClick={() => {
                    this.setState({
                      navigation: nav.serviceRequestNavigation,
                      navigationHistory: nav.previousPageHistory
                    });
                  }}
                  large
                >
                  {t('Save')}
                </RaisedButton>
              )}
            </ServiceRequestNavContext.Consumer>
          </>
        )}
      </ServiceRequestFooter>
    );
  }

  getEarlyConfirmFooter(serviceRequestId, dirty, isSubmitting) {
    const { t } = this.props;

    return (
      <ServiceRequestFooter
        message={t(
          'Early confirmation is required for this service request. Please confirm, change or remove this request.'
        )}
      >
        <GhostButton danger onClick={this.onRemove} large>
          {t('Remove this service request')}
        </GhostButton>
        <div>
          <ServiceRequestNavContext.Consumer>
            {nav => (
              <GhostButton
                onClick={() =>
                  this.cancelForm(dirty, nav.serviceRequestNavigation)
                }
                large
              >
                {t('Cancel')}
              </GhostButton>
            )}
          </ServiceRequestNavContext.Consumer>
          <ServiceRequestNavContext.Consumer>
            {/* remporary workaround for when the confirm button is not showing */}
            {/* {nav =>
              !this.formHasValueChanges(dirty) ? (
                <RaisedButton
                  type="button"
                  onClick={() => this.onConfirmServiceRequest(serviceRequestId)}
                  disabled={this.state.isEarlyConfirmationSubmitting}
                  large
                >
                  {t('Confirm')}
                </RaisedButton>
              ) : (
                <RaisedButton
                  type="submit"
                  disabled={isSubmitting}
                  onClick={() => {
                    this.setState({
                      navigation: nav.serviceRequestNavigation,
                      navigationHistory: nav.previousPageHistory
                    });
                  }}
                  large
                >
                  {t('Save')}
                </RaisedButton>
              )
            } */}
            {nav => (
              <>
                <RaisedButton
                  type="submit"
                  disabled={isSubmitting}
                  onClick={() => {
                    this.setState({
                      navigation: nav.serviceRequestNavigation,
                      navigationHistory: nav.previousPageHistory
                    });
                  }}
                  large
                >
                  {t('Save')}
                </RaisedButton>

                <RaisedButton
                  type="button"
                  onClick={() => this.onConfirmServiceRequest(serviceRequestId)}
                  disabled={this.state.isEarlyConfirmationSubmitting}
                  large
                >
                  {t('Confirm')}
                </RaisedButton>
              </>
            )}
          </ServiceRequestNavContext.Consumer>
        </div>
      </ServiceRequestFooter>
    );
  }

  getEarlyConfirmFooterForDisabled(serviceRequestId) {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Early confirmation is required for this service request. Please confirm this request.'
        )}
      >
        <RaisedButton
          type="button"
          onClick={() => this.onConfirmServiceRequest(serviceRequestId)}
          disabled={this.state.isEarlyConfirmationSubmitting}
          large
        >
          {t('Confirm')}
        </RaisedButton>
      </ServiceRequestFooter>
    );
  }

  getEarlyConfirmNoPermissionFooterForDisabled() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Early confirmation to this service request required. Waiting for confirmation by SN responsible.'
        )}
      ></ServiceRequestFooter>
    );
  }

  getDefaultFooter(dirty, isSubmitting) {
    const { t } = this.props;
    return (
      <>
        <GhostButton danger onClick={this.onRemove} large>
          {t('Remove this service request')}
        </GhostButton>
        <div>
          <ServiceRequestNavContext.Consumer>
            {nav => (
              <GhostButton
                onClick={() =>
                  this.cancelForm(dirty, nav.serviceRequestNavigation)
                }
                large
              >
                {t('Cancel')}
              </GhostButton>
            )}
          </ServiceRequestNavContext.Consumer>
          <ServiceRequestNavContext.Consumer>
            {nav => (
              <RaisedButton
                type="submit"
                disabled={(!dirty && !this.state.itemChanged) || isSubmitting}
                onClick={() => {
                  this.setState({
                    navigation: nav.serviceRequestNavigation,
                    navigationHistory: nav.previousPageHistory
                  });
                }}
                large
              >
                {t('Save')}
              </RaisedButton>
            )}
          </ServiceRequestNavContext.Consumer>
        </div>
      </>
    );
  }

  canUserCreateTicket() {
    return !this.isOnlyPreviewMode();
  }

  getNoTicketsFooter(footer) {
    const { t } = this.props;
    return (
      <ServiceRequestFooter>
        {this.canUserCreateTicket() && (
          <>
            <RaisedButton
              type="button"
              large
              iconLeft={<Icon icon="plus" />}
              disabled={footer.isChangeDisabled}
              onClick={() => {
                this.setState({
                  showCreateChangeRequestDialog: true
                });
              }}
            >
              {t('Add change request')}
            </RaisedButton>
            <GhostButton
              type="button"
              danger
              large
              disabled={footer.isCancellationDisabled}
              onClick={() => {
                this.setState({
                  showRequestCancellationDialog: true
                });
              }}
              iconLeft={<Icon icon="Close" />}
            >
              {t('Request for cancellation')}
            </GhostButton>
          </>
        )}
      </ServiceRequestFooter>
    );
  }

  rejectChangeRequestResponse = () => {
    const { t, match, changeRequestRejectResponse } = this.props;
    Confirm(
      t('Reject response'),
      t("Are you sure, you want to reject HN's response?"),
      () => {
        changeRequestRejectResponse(match.params.serviceRequestId).then(
          response => {
            if (response.success) {
              Toast.success(t('Change request successfully rejected'));
              this.loadComponentData();
            } else if (response.payload) {
              Toast.warning(response.payload.message);
            } else {
              Toast.warning(t('Cannot reject response'));
            }
          }
        );
      },
      () => {},
      t('Reject'),
      t('Close')
    );
  };

  acceptChangeRequestResponse = () => {
    const { t, match, changeRequestAcceptResponse } = this.props;
    Confirm(
      t('Accept response'),
      t("Are you sure, you want to accept HN's response? "),
      () => {
        changeRequestAcceptResponse(match.params.serviceRequestId).then(
          response => {
            if (response.success) {
              Toast.success(t('Change request successfully confirmed'));
              this.loadComponentData();
            } else if (response.payload) {
              Toast.warning(response.payload.message);
            } else {
              Toast.warning(t('Cannot accept response'));
            }
          }
        );
      },
      () => {},
      t('Accept'),
      t('Close')
    );
  };

  getChangeAcceptFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'There is new response for this service request. Please accept HN response in order to confirm requested changes.'
        )}
      >
        {this.canUserAcceptResponse() && (
          <>
            <GhostButton
              type="button"
              danger
              large
              onClick={this.rejectChangeRequestResponse}
            >
              {t('Reject')}
            </GhostButton>
            <RaisedButton
              type="button"
              large
              onClick={this.acceptChangeRequestResponse}
            >
              {t('Accept')}
            </RaisedButton>
          </>
        )}
      </ServiceRequestFooter>
    );
  }

  getAdditionalChangeWaitingForHnFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Changes for this service request are not confirmed yet. Waiting for response from the HN.'
        )}
      />
    );
  }

  getCancellationWaitingForHnFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Cancellation of this service request is not confirmed yet. Waiting for response from the HN.'
        )}
      />
    );
  }

  getAdditionalCreateWaitingForSnFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Response to this service request is not accepted yet. Waiting for confirmation by SN responsible.'
        )}
      />
    );
  }

  getAdditionalChangeWaitingForSnFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Response to this service request is not accepted yet. Waiting for confirmation by SN responsible.'
        )}
      />
    );
  }

  onConfirmServiceRequest(serviceRequestId) {
    this.setState({ isEarlyConfirmationSubmitting: true });
    const { history } = this.props;

    this.props.confirmServiceRequest(serviceRequestId).then(response => {
      if (response.success) {
        Toast.success('Service request confirmed');
        history.push(this.getBackNavigationUrl(this.state.navigation));
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      } else {
        Toast.warning('Failed to confirm service request');
      }
      this.setState({ isEarlyConfirmationSubmitting: false });
    });
  }

  onDeliveryFileDialogOpen = () => {
    this.setState({
      deliveryFileDialogOpen: true
    });
  };

  onDeliveryFileDialogClose = () => {
    this.setState({
      deliveryFileDialogOpen: false
    });
  };

  onCancellationFileDialogOpen = () => {
    this.setState({
      cancellationFileDialogOpen: true
    });
  };

  onCancellationFileDialogClose = () => {
    this.setState({
      cancellationFileDialogOpen: false
    });
  };

  createRequestCancellation = (
    values,
    { setSubmitting, setErrors },
    setDialogError,
    onDialogClose
  ) => {
    const { t, createCancellationRequest, match } = this.props;
    const data = {
      ...values,
      serviceRequestId: match.params.serviceRequestId
    };

    createCancellationRequest(data.serviceRequestId, data).then(response => {
      if (response.success) {
        Toast.success(t('Request for cancellation submitted successfully'));
        onDialogClose();
        this.loadComponentData();
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [error.target]: error.message[0]
          };
        }, {});
        if (errors) {
          setErrors(errors);
        }
      } else if (response.payload) {
        setDialogError(response.payload.message);
      } else {
        setDialogError(t('Cannot create request for cancellation'));
      }
      setSubmitting(false);
    });
  };

  renderRequestCancellationDialog() {
    const { t } = this.props;
    const { showRequestCancellationDialog } = this.state;
    return (
      <ActionWithCommentDialog
        title={t('Request for cancellation')}
        commentLabel={t('Comment (why do you want to cancel service request)')}
        confirmText={t('Submit')}
        reload={this.getAssignmentServiceRequest}
        isDialogOpen={showRequestCancellationDialog}
        onSubmit={this.createRequestCancellation}
        closeDialog={() => {
          this.setState({
            showRequestCancellationDialog: false
          });
        }}
      />
    );
  }

  getInitialActivity = () => {
    if (this.state.serviceRequest && this.state.serviceRequest.activityId) {
      return this.state.serviceRequest.activityId;
    }
    return null;
  };

  isActivityNameDifferent() {
    return this.props.forSubmitData.activities &&
      this.props.forSubmitData.activities.find(
        x =>
          x.activityId === this.state.serviceRequest.activityId &&
          x.name !== this.state.serviceRequest.activityName
      )
      ? true
      : false;
  }

  renderHeaderCenterActions = () => {
    return null;
  };

  canRollback = () => {
    return false;
  };

  renderRollbackFooter = () => {
    return null;
  };

  shouldRenderRollbackFooter = () => {
    return false;
  };

  render() {
    const { t, match, footer, forSubmitData } = this.props;
    const { disabled, serviceRequest } = this.state;
    if (this.props.isLoading) {
      return null;
    }

    const defaultUom =
      objectPath
        .get(serviceRequest, 'unitsOfMeasure', [])
        .find(item => item.isDefault) || {};

    if (
      (!serviceRequest.activityId === null &&
        forSubmitData.activities &&
        forSubmitData.activities.length !== 0) ||
      this.isActivityNameDifferent()
    ) {
      this.setState({
        itemChanged: true
      });
    }

    const editedFields = objectPath.get(serviceRequest, 'editedFields', []);

    return (
      <div className="service-request-edit--container" ref={this.container}>
        <ChangeRequestCreateDialog
          isDialogOpen={this.state.showCreateChangeRequestDialog}
          reload={this.loadComponentData}
          closeDialog={() => {
            this.setState({
              showCreateChangeRequestDialog: false
            });
          }}
          unitsOptions={
            serviceRequest
              ? this.getUnits(serviceRequest.units).map(unit => {
                  return (
                    <option value={unit.value} key={unit.value}>
                      {unit.title}
                    </option>
                  );
                })
              : []
          }
          uomsOptions={
            serviceRequest && serviceRequest.unitsOfMeasure
              ? this.toUomsEntity(serviceRequest.unitsOfMeasure).map(uom => {
                  return (
                    <option value={uom.value} key={uom.value}>
                      {uom.title}
                    </option>
                  );
                })
              : []
          }
          activities={forSubmitData.activities}
          serviceRequest={serviceRequest}
          serviceRequestId={serviceRequest.serviceRequestId}
        />
        {this.renderRequestCancellationDialog()}
        <Formik
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={this.validationSchema}
          enableReinitialize={true}
          initialValues={{
            ...serviceRequest,
            activityId: this.getInitialActivity(),
            quantity: serviceRequest.requestedQuantity
          }}
          onSubmit={this.onSubmit}
        >
          {({
            handleReset,
            handleSubmit,
            setFieldValue,
            values,
            dirty,
            isSubmitting
          }) => (
            <>
              <div className="service-request-edit--heading">
                <ServiceRequestNavContext.Consumer>
                  {nav => (
                    <>
                      {this.setState({
                        navigation: nav.serviceRequestNavigation,
                        navigationHistory: nav.previousPageHistory
                      })}
                      <PageHeader
                        to={this.getBackNavigationUrl(
                          nav.serviceRequestNavigation
                        )}
                        onClick={event => {
                          event.preventDefault();
                          this.cancelForm(dirty, nav.serviceRequestNavigation);
                        }}
                        title={
                          t(
                            disabled
                              ? 'Preview service request'
                              : 'Edit service request'
                          ) + ' - '
                        }
                        unit={{
                          code: serviceRequest.partyCode,
                          name: serviceRequest.unitName,
                          number: serviceRequest.requestNumber
                        }}
                        status={{
                          name: serviceRequest.statusGridName,
                          id: serviceRequest.statusId
                        }}
                        parent={this.getServiceRequestParent()}
                      />
                    </>
                  )}
                </ServiceRequestNavContext.Consumer>
                {this.renderHeaderCenterActions()}
                <PageLinks
                  isDirty={dirty}
                  isTransportation={serviceRequest.originalIsTransportation}
                  serviceRequestId={match.params.serviceRequestId}
                  partyId={match.params.partyId}
                  discussion={{
                    status: serviceRequest.discussionStatus,
                    initials: this.getDiscussionInitials(),
                    disabled: disabled,
                    onDiscussionClose: this.loadComponentData,
                    isSeen: serviceRequest.isLastDiscussionSeen
                  }}
                  transportationPath={this.getTransportationPath()}
                  changeHistoryPath={this.getChangeHistoryPath()}
                  showPath={this.getShowPath()}
                  editPath={this.getEditPath()}
                  historyPath={this.getHistoryPath()}
                  discussionPath={this.getDiscussionPath(
                    PARTY_TYPE.SendingNation
                  )}
                />
              </div>
              <form onReset={handleReset} onSubmit={handleSubmit}>
                <ServiceRequestNavContext.Consumer>
                  {nav =>
                    nav.serviceRequestId !== match.params.serviceRequestId &&
                    nav.setServiceRequestId(match.params.serviceRequestId)
                  }
                </ServiceRequestNavContext.Consumer>
                <ServiceRequestNavContext.Consumer>
                  {nav =>
                    this.props.withEscNavigationCallback(
                      this.navigateBack(nav.serviceRequestNavigation)
                    )
                  }
                </ServiceRequestNavContext.Consumer>
                <PageActions container={this.container.current}>
                  {!disabled &&
                    (serviceRequest.response &&
                    serviceRequest.response.earlyConfirmation
                      .isEarlyConfirmationRequired &&
                    serviceRequest.response.earlyConfirmation.isEditable ? (
                      this.getEarlyConfirmFooter(
                        serviceRequest.serviceRequestId,
                        dirty,
                        isSubmitting
                      )
                    ) : this.isAdditionalRequest &&
                      footer &&
                      (footer.footerType ===
                        SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateWaitingForHn ||
                        footer.footerType ===
                          SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateConfirm ||
                        footer.footerType ===
                          SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateAccept) ? (
                      <>
                        {(footer.footerType ===
                          SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateWaitingForHn ||
                          footer.footerType ===
                            SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateConfirm) &&
                          this.getAdditionalEditableCreateWaitingForHnFooter(
                            dirty,
                            isSubmitting
                          )}
                        {footer.footerType ===
                          SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateAccept &&
                          this.getAdditionalEditableCreateAcceptFooter(
                            dirty,
                            isSubmitting
                          )}
                      </>
                    ) : (
                      this.getDefaultFooter(dirty, isSubmitting)
                    ))}
                  {disabled && footer && (
                    <>
                      {footer.footerType ===
                        SERVICE_REQUEST_FOOTER_STATUSES.None &&
                        serviceRequest.response &&
                        serviceRequest.response.earlyConfirmation
                          .isEarlyConfirmationRequired &&
                        serviceRequest.response.earlyConfirmation.isEditable &&
                        this.getEarlyConfirmFooterForDisabled(
                          serviceRequest.serviceRequestId
                        )}
                      {footer.footerType ===
                        SERVICE_REQUEST_FOOTER_STATUSES.None &&
                        serviceRequest.response &&
                        serviceRequest.response.earlyConfirmation
                          .isEarlyConfirmationRequired &&
                        !serviceRequest.response.earlyConfirmation.isEditable &&
                        this.getEarlyConfirmNoPermissionFooterForDisabled()}
                      {(footer.footerType ===
                        SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateWaitingForHn ||
                        footer.footerType ===
                          SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateConfirm) &&
                        this.getAdditionalCreateWaitingForHnFooter(
                          dirty,
                          isSubmitting,
                          serviceRequest.canEditItem
                        )}
                      {footer.footerType ===
                        SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateAccept &&
                        this.getAdditionalCreateAcceptFooter()}
                      {footer.footerType ===
                        SERVICE_REQUEST_FOOTER_STATUSES.RequestForCancellation &&
                        this.getNoTicketsFooter(footer)}
                      {(footer.footerType ===
                        SERVICE_REQUEST_FOOTER_STATUSES.AdditionalChangeWaitingForHn ||
                        footer.footerType ===
                          SERVICE_REQUEST_FOOTER_STATUSES.AdditionalChangeConfirm) &&
                        this.getAdditionalChangeWaitingForHnFooter()}
                      {footer.footerType ===
                        SERVICE_REQUEST_FOOTER_STATUSES.AdditionalChangeAccept &&
                        this.getChangeAcceptFooter()}
                      {(footer.footerType ===
                        SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCancellationWaitingForHn ||
                        footer.footerType ===
                          SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCancellationConfirm) &&
                        this.getCancellationWaitingForHnFooter()}
                      {footer.footerType ===
                        SERVICE_REQUEST_FOOTER_STATUSES.AdditionalChangeWaitingForSn &&
                        this.getAdditionalChangeWaitingForSnFooter()}
                      {footer.footerType ===
                        SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateWaitingForSn &&
                        this.getAdditionalCreateWaitingForSnFooter()}
                    </>
                  )}
                  {this.shouldRenderRollbackFooter(disabled, footer) &&
                    this.renderRollbackFooter()}
                </PageActions>
                <div className="service-request-edit--panels">
                  <div>
                    <Panel>
                      <div className="service-request-edit--panel service-request-edit--panel--edit">
                        <div className="service-request-edit--panel-top">
                          <H2>{t('Item information')}</H2>
                          {(!disabled || serviceRequest.canEditItem) && (
                            <div className="service-request-edit--change-item">
                              {this.state.showChangeItem && (
                                <Field
                                  placeholder={t('Search items')}
                                  name="changeItem"
                                  items={this.toAutocompleteEntity(
                                    this.props.oData.data,
                                    'Code',
                                    'Name',
                                    true
                                  )}
                                  onItemSelect={selectedValues => {
                                    this.changeItem(
                                      selectedValues,
                                      setFieldValue
                                    );
                                  }}
                                  onQueryChange={this.onItemQueryChange}
                                  inputClass="service-request-edit--change-item-input"
                                  component={Autocomplete}
                                  noResultsMessage={t(
                                    'No items found in the Catalog'
                                  )}
                                />
                              )}
                              {serviceRequest.catalogItemCanBeChanged && (
                                <GhostButton onClick={this.toggleChangeItem}>
                                  {this.state.showChangeItem
                                    ? t('Cancel')
                                    : t('Change item')}
                                </GhostButton>
                              )}
                            </div>
                          )}
                        </div>
                        <CatalogItemInfo
                          title={`${serviceRequest.code} ${serviceRequest.name}`}
                          description={serviceRequest.description}
                          rateFrom={serviceRequest.reateFrom}
                          rateTo={serviceRequest.reateTo}
                          currency={serviceRequest.currency}
                          uom={serviceRequest.unitOfMeasureName}
                          attachments={serviceRequest.catalogItemAttachments}
                          groupName={serviceRequest.groupName}
                          annexName={serviceRequest.annexName}
                          partyId={match.params.partyId}
                          categoryServiceRequests={
                            serviceRequest.categoryServiceRequests
                          }
                          annexinformation={
                            this.props.forSubmitData.annexes &&
                            this.props.forSubmitData.annexes.find(
                              x =>
                                x.annexId == this.state.serviceRequest.annexId
                            ) &&
                            !disabled
                              ? this.props.forSubmitData.annexes.find(
                                  x =>
                                    x.annexId ==
                                    this.state.serviceRequest.annexId
                                ).information
                              : null
                          }
                          catalogItemId={serviceRequest.catalogItemId}
                          tooltipStartDisabled={!this.state.itemChanged}
                          editedFields={editedFields}
                        />
                        <Field name="itemChanged" type="hidden" />
                      </div>
                    </Panel>
                    <Panel>
                      <div className="service-request-edit--panel">
                        <H2>{t('Additional information')}</H2>
                        <Field
                          name="comment"
                          setFieldValue={setFieldValue}
                          placeholder={t('Describe additional details')}
                          label={t('Comment')}
                          component={Textarea}
                          disabled={disabled}
                          indicationMessage={FieldHistoryMessage(
                            'Comment',
                            editedFields
                          )}
                        />

                        <div className="service-request-edit--documents-section">
                          <InputLabel
                            indicationMessage={FieldHistoryMessage(
                              'AttachmentNames',
                              editedFields
                            )}
                          >
                            {t('Attachments')}:
                          </InputLabel>
                          <DropZone
                            value={this.props.attachments}
                            onChange={files => {
                              setFieldValue('attachments', files);
                            }}
                            onSelect={files => {
                              this.handleFileUpload(files);
                            }}
                            onRemove={file => {
                              this.props.deleteAttachment(file);
                            }}
                            disabled={disabled}
                          />
                        </div>
                      </div>
                    </Panel>
                    <GeneralAttachments
                      attachments={objectPath.get(
                        values,
                        'generalAttachments',
                        []
                      )}
                      onFileSelect={this.handleFileUpload}
                      disabled={true}
                    />
                  </div>
                  <Panel>
                    <div className="service-request-edit--panel">
                      <H2>{t('Delivery information')}</H2>
                      <DeliveryInformation
                        uoms={this.toUomsEntity(
                          objectPath.get(serviceRequest, 'unitsOfMeasure', [])
                        )}
                        showTotalEstimates={
                          values.unitOfMeasureId === defaultUom.unitOfMeasureId
                        }
                        activities={this.toActivityEntity(
                          this.props.forSubmitData.activities || []
                        )}
                        units={this.getUnits(this.props.forSubmitData.units)}
                        setFieldValue={setFieldValue}
                        results={this.props.calculateByDaysData}
                        checked={values.isCalculatedByDays}
                        disabled={disabled}
                        editedFields={editedFields}
                        values={values}
                        showCurrency={true}
                        exerciseCurrency={serviceRequest.currency}
                      />

                      {this.calculateCosts(values)}

                      {serviceRequest.isTransportation === false && (
                        <>
                          <H5>{t('Location details')}</H5>
                          <LocationDetails
                            initialLocation={{
                              original: {
                                latitude: serviceRequest.latitude,
                                longtitude: serviceRequest.longitude,
                                site: serviceRequest.site,
                                name: serviceRequest.locationName,
                                region: {
                                  name: serviceRequest.regionName
                                }
                              }
                            }}
                            defaultQuery={
                              serviceRequest.locationName
                                ? serviceRequest.locationName
                                : ''
                            }
                            setFieldValue={setFieldValue}
                            locationItems={this.toAutocompleteEntity(
                              this.props.query
                            )}
                            onQueryChange={this.onLocationQueryChange}
                            disabled={disabled}
                            editedFieldsHistory={{
                              location: FieldHistoryMessage(
                                'LocationFrom',
                                editedFields
                              ),
                              locationComment: FieldHistoryMessage(
                                'LocationCommentFrom',
                                editedFields
                              ),
                              region: FieldHistoryMessage(
                                'RegionNameFrom',
                                editedFields
                              ),
                              longitude: FieldHistoryMessage(
                                'LongitudeFrom',
                                editedFields
                              ),
                              latitude: FieldHistoryMessage(
                                'LatitudeFrom',
                                editedFields
                              ),
                              site: FieldHistoryMessage(
                                'SiteFrom',
                                editedFields
                              )
                            }}
                          />
                        </>
                      )}

                      {serviceRequest.isTransportation === true && (
                        <>
                          <H5>{t('Pickup location')}</H5>
                          <LocationDetails
                            initialLocation={{
                              original: {
                                latitude: serviceRequest.latitudeFrom,
                                longtitude: serviceRequest.longitudeFrom,
                                site: serviceRequest.siteFrom,
                                name: serviceRequest.locationName,
                                region: {
                                  name: serviceRequest.regionNameFrom
                                }
                              }
                            }}
                            defaultQuery={
                              serviceRequest.locationNameFrom
                                ? serviceRequest.locationNameFrom
                                : ''
                            }
                            setFieldValue={setFieldValue}
                            locationItems={this.toAutocompleteEntity(
                              this.props.query
                            )}
                            onQueryChange={this.onLocationQueryChange}
                            isExpandable={true}
                            schema={{
                              location: 'locationFrom',
                              locationComment: 'locationCommentFrom'
                            }}
                            disabled={disabled}
                            editedFieldsHistory={{
                              location: FieldHistoryMessage(
                                'LocationFrom',
                                editedFields
                              ),
                              locationComment: FieldHistoryMessage(
                                'LocationCommentFrom',
                                editedFields
                              ),
                              region: FieldHistoryMessage(
                                'RegionNameFrom',
                                editedFields
                              ),
                              longitude: FieldHistoryMessage(
                                'LongitudeFrom',
                                editedFields
                              ),
                              latitude: FieldHistoryMessage(
                                'LatitudeFrom',
                                editedFields
                              ),
                              site: FieldHistoryMessage(
                                'SiteFrom',
                                editedFields
                              )
                            }}
                          />
                          <H5>{t('Delivery location')}</H5>
                          <LocationDetails
                            initialLocation={{
                              original: {
                                latitude: serviceRequest.latitude,
                                longtitude: serviceRequest.longitude,
                                site: serviceRequest.site,
                                name: serviceRequest.locationName,
                                region: {
                                  name: serviceRequest.regionName
                                }
                              }
                            }}
                            defaultQuery={
                              serviceRequest.locationName
                                ? serviceRequest.locationName
                                : ''
                            }
                            setFieldValue={setFieldValue}
                            locationItems={this.toAutocompleteEntity(
                              this.props.query
                            )}
                            onQueryChange={this.onLocationQueryChange}
                            isExpandable={true}
                            disabled={disabled}
                            editedFieldsHistory={{
                              location: FieldHistoryMessage(
                                'LocationTo',
                                editedFields
                              ),
                              locationComment: FieldHistoryMessage(
                                'LocationCommentTo',
                                editedFields
                              ),
                              region: FieldHistoryMessage(
                                'RegionNameTo',
                                editedFields
                              ),
                              longitude: FieldHistoryMessage(
                                'LongitudeTo',
                                editedFields
                              ),
                              latitude: FieldHistoryMessage(
                                'LatitudeTo',
                                editedFields
                              ),
                              site: FieldHistoryMessage('SiteTo', editedFields)
                            }}
                          />
                        </>
                      )}
                    </div>
                  </Panel>
                </div>
              </form>
            </>
          )}
        </Formik>
        {serviceRequest &&
          !serviceRequest.isCategory &&
          serviceRequest.response &&
          serviceRequest.response.canDeliveryQuantity >= 0 && (
            <div>
              <H2>{t('Response from HN')}</H2>

              <AssignmentResponse
                key={ShortId.generate()}
                assignmentResponse={serviceRequest.response}
                disabled={true}
                isFileDialogOpen={this.state.responseFileDialogOpen}
                onFileDialogOpen={this.onResponseFileDialogOpen}
                onFileDialogClose={this.onResponseFileDialogClose}
                isDeliveryFileDialogOpen={this.state.deliveryFileDialogOpen}
                onDeliveryFileDialogOpen={this.onDeliveryFileDialogOpen}
                onDeliveryFileDialogClose={this.onDeliveryFileDialogClose}
                showCurrency={true}
                exerciseCurrency={serviceRequest.currency}
              />
              {serviceRequest.deliveryLines.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <H2>{t('Delivery from HN')}</H2>

                  <AssignmentDelivery
                    key={ShortId.generate()}
                    deliveryLines={serviceRequest.deliveryLines}
                    disabled={true}
                    isFileDialogOpen={this.state.responseFileDialogOpen}
                    onFileDialogOpen={this.onResponseFileDialogOpen}
                    onFileDialogClose={this.onResponseFileDialogClose}
                    isDeliveryFileDialogOpen={this.state.deliveryFileDialogOpen}
                    onDeliveryFileDialogOpen={this.onDeliveryFileDialogOpen}
                    onDeliveryFileDialogClose={this.onDeliveryFileDialogClose}
                    showCurrency={true}
                    exerciseCurrency={serviceRequest.currency}
                    totals={serviceRequest.deliveryLineTotals}
                  />
                </div>
              )}
              {serviceRequest.cancellation && (
                <CancellationCosts
                  cancellation={serviceRequest.cancellation}
                  exerciseCurrency={serviceRequest.currency}
                  isCancellationFileDialogOpen={
                    this.state.cancellationFileDialogOpen
                  }
                  onCancellationFileDialogOpen={
                    this.onCancellationFileDialogOpen
                  }
                  onCancellationFileDialogClose={
                    this.onCancellationFileDialogClose
                  }
                  isPreviewMode={this.isOnlyPreviewMode()}
                />
              )}
            </div>
          )}
        {serviceRequest && serviceRequest.changeRequestResponse && (
          <div>
            <H2>
              <InputLabel
                indicationMessage={t('Response from HN to change request')}
              >
                {t('Response to change request')}
              </InputLabel>
            </H2>
            <AssignmentResponse
              key={ShortId.generate()}
              assignmentResponse={serviceRequest.changeRequestResponse}
              disabled={true}
              cancellation={serviceRequest.cancellation}
              isFileDialogOpen={this.state.changeRequestResponseFileDialogOpen}
              onFileDialogOpen={this.onChangeRequestResponseFileDialogOpen}
              onFileDialogClose={this.onChangeRequestResponseFileDialogClose}
              isDeliveryFileDialogOpen={this.state.deliveryFileDialogOpen}
              onDeliveryFileDialogOpen={this.onDeliveryFileDialogOpen}
              onDeliveryFileDialogClose={this.onDeliveryFileDialogClose}
              showCurrency={true}
              exerciseCurrency={serviceRequest.currency}
            />
          </div>
        )}
      </div>
    );
  }
}

ServiceRequestEdit.propTypes = {
  oData: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  serviceRequest: PropTypes.object,
  forSubmitData: PropTypes.object,
  query: PropTypes.array,
  isLoading: PropTypes.bool,
  t: PropTypes.func,
  forSubmit: PropTypes.func,
  queryLocations: PropTypes.func,
  queryOData: PropTypes.func,
  resetOData: PropTypes.func,
  update: PropTypes.func,
  remove: PropTypes.func,
  getServiceRequest: PropTypes.func,
  confirmServiceRequest: PropTypes.func,
  uploadAttachments: PropTypes.func,
  setAttachments: PropTypes.func,
  deleteAttachment: PropTypes.func,
  clearAttachments: PropTypes.func,
  attachments: PropTypes.array,
  location: PropTypes.object,
  hasUserPermission: PropTypes.func,
  calculateByDaysData: PropTypes.object,
  calculateByDays: PropTypes.func,
  getFooter: PropTypes.func,
  footer: PropTypes.object,
  createRejectResponse: PropTypes.func,
  createAcceptResponse: PropTypes.func,
  changeRequestRejectResponse: PropTypes.func,
  changeRequestAcceptResponse: PropTypes.func,
  createCancellationRequest: PropTypes.func,
  clearFooter: PropTypes.func,
  withEscNavigationCallback: PropTypes.func
};

export default ServiceRequestEdit;
